import { CalendarRange, ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { ErrorState, Skeleton } from "../../components/ui/Feedback";
import { addDays, daysBetween, startOfDay, zonedDay } from "../../lib/dates";
import { useShopTimeZone } from "../settings/useSettings";
import { cn } from "../../lib/cn";
import type { Campaign, CampaignStatus } from "../../lib/types";
import { useCampaigns } from "./useCampaigns";

const BAR_STYLES: Record<CampaignStatus, string> = {
	active: "bg-ink text-canvas",
	draft: "border border-dashed border-line-strong bg-surface text-ink-muted",
	ended: "bg-raised text-ink-faint",
};

const monthTitleFormatter = new Intl.DateTimeFormat(undefined, {
	month: "long",
	year: "numeric",
});
const weekdayFormatter = new Intl.DateTimeFormat(undefined, { weekday: "short" });

const WEEKDAYS = Array.from({ length: 7 }, (_, index) =>
	weekdayFormatter.format(new Date(2024, 0, 7 + index)),
);

const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
const startOfWeek = (date: Date) => addDays(date, -date.getDay());

interface Scheduled {
	campaign: Campaign;
	starts: Date;
	ends: Date;
}

interface Segment extends Scheduled {
	startCol: number;
	span: number;
	continuesLeft: boolean;
	continuesRight: boolean;
	lane: number;
}

/** Placed on the days it runs in the shop's time zone, whoever is looking. */
const toScheduled = (campaign: Campaign, timeZone: string): Scheduled | null => {
	const starts = zonedDay(campaign.startsAt, timeZone);
	if (!starts) return null;
	const ends = zonedDay(campaign.endsAt, timeZone) ?? starts;
	return { campaign, starts, ends };
};

const segmentsForWeek = (scheduled: readonly Scheduled[], weekStart: Date): Segment[] => {
	const weekEnd = addDays(weekStart, 6);

	const overlapping = scheduled
		.filter((entry) => entry.ends >= weekStart && entry.starts <= weekEnd)
		.map((entry) => {
			const from = entry.starts < weekStart ? weekStart : entry.starts;
			const to = entry.ends > weekEnd ? weekEnd : entry.ends;
			return {
				...entry,
				startCol: daysBetween(weekStart, from),
				span: daysBetween(from, to) + 1,
				continuesLeft: entry.starts < weekStart,
				continuesRight: entry.ends > weekEnd,
				lane: 0,
			};
		})
		.sort((left, right) => left.startCol - right.startCol || right.span - left.span);

	const laneEnds: number[] = [];
	for (const segment of overlapping) {
		let lane = laneEnds.findIndex((end) => end < segment.startCol);
		if (lane === -1) {
			lane = laneEnds.length;
			laneEnds.push(-1);
		}
		laneEnds[lane] = segment.startCol + segment.span - 1;
		segment.lane = lane;
	}

	return overlapping;
};

const Legend = () => (
	<div className="flex flex-wrap items-center gap-4 text-xs text-ink-muted">
		{(["active", "draft", "ended"] as CampaignStatus[]).map((status) => (
			<span key={status} className="flex items-center gap-1.5">
				<span className={cn("h-3 w-6 rounded-sm", BAR_STYLES[status])} />
				{status.charAt(0).toUpperCase() + status.slice(1)}
			</span>
		))}
	</div>
);

export const CampaignCalendar = () => {
	const campaigns = useCampaigns();
	const { timeZone } = useShopTimeZone();
	const [month, setMonth] = useState(() => startOfMonth(new Date()));
	const today = startOfDay(new Date());

	const weeks = useMemo(() => {
		const gridStart = startOfWeek(startOfMonth(month));
		const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0);
		const weekCount = Math.ceil((daysBetween(gridStart, monthEnd) + 1) / 7);
		return Array.from({ length: weekCount }, (_, index) => addDays(gridStart, index * 7));
	}, [month]);

	if (campaigns.isPending) return <Skeleton className="h-96 w-full" />;
	if (campaigns.isError) {
		return <ErrorState title="Could not load campaigns" description={campaigns.error.message} />;
	}

	const all = campaigns.data?.items ?? [];
	const scheduled = all
		.map((campaign) => toScheduled(campaign, timeZone))
		.filter((entry): entry is Scheduled => entry !== null);
	const unscheduled = all.filter((campaign) => !campaign.startsAt);

	return (
		<section className="space-y-4">
			<div className="flex flex-wrap items-end justify-between gap-3">
				<div>
					<h3 className="flex items-center gap-2 text-sm font-medium text-ink">
						<CalendarRange className="h-4 w-4 text-ink-muted" strokeWidth={1.75} />
						Campaign calendar
					</h3>
					<p className="mt-0.5 text-xs text-ink-muted">{monthTitleFormatter.format(month)}</p>
				</div>
				<div className="flex items-center gap-2">
					<Legend />
					<div className="ml-2 flex items-center gap-1">
						<Button
							variant="outline"
							size="sm"
							aria-label="Previous month"
							onClick={() => setMonth((current) => startOfMonth(addDays(current, -1)))}
						>
							<ChevronLeft className="h-3.5 w-3.5" strokeWidth={2} />
						</Button>
						<Button variant="outline" size="sm" onClick={() => setMonth(startOfMonth(new Date()))}>
							Today
						</Button>
						<Button
							variant="outline"
							size="sm"
							aria-label="Next month"
							onClick={() => setMonth((current) => startOfMonth(addDays(current, 32)))}
						>
							<ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
						</Button>
					</div>
				</div>
			</div>

			<div className="overflow-hidden rounded-md border border-line bg-surface">
				<div className="grid grid-cols-7 border-b border-line">
					{WEEKDAYS.map((day) => (
						<div key={day} className="px-2 py-1.5 text-xs font-medium text-ink-faint">
							{day}
						</div>
					))}
				</div>

				{weeks.map((weekStart) => {
					const segments = segmentsForWeek(scheduled, weekStart);
					const laneCount = segments.reduce((max, segment) => Math.max(max, segment.lane + 1), 0);

					return (
						<div
							key={weekStart.toISOString()}
							className="grid grid-cols-7"
							style={{ gridTemplateRows: `auto repeat(${laneCount}, auto)` }}
						>
							{Array.from({ length: 7 }, (_, index) => {
								const day = addDays(weekStart, index);
								const isCurrentMonth = day.getMonth() === month.getMonth();
								return (
									<div
										key={index}
										style={{ gridColumn: index + 1, gridRow: "1 / -1" }}
										className={cn(
											"min-h-24 border-r border-b border-line last:border-r-0",
											isCurrentMonth ? "bg-surface" : "bg-canvas",
										)}
									/>
								);
							})}

							{Array.from({ length: 7 }, (_, index) => {
								const day = addDays(weekStart, index);
								const isCurrentMonth = day.getMonth() === month.getMonth();
								const isToday = day.getTime() === today.getTime();
								return (
									<div
										key={`n${index}`}
										style={{ gridColumn: index + 1, gridRow: 1 }}
										className="relative z-10 px-1.5 pt-1.5 pb-1"
									>
										<span
											className={cn(
												"inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs",
												isToday
													? "bg-ink font-medium text-canvas"
													: isCurrentMonth
														? "text-ink-muted"
														: "text-ink-faint",
											)}
										>
											{day.getDate()}
										</span>
									</div>
								);
							})}

							{segments.map((segment) => (
								<Link
									key={`${segment.campaign.id}-${weekStart.toISOString()}`}
									to={`/campaigns/${segment.campaign.id}`}
									title={`${segment.campaign.title} — ${segment.campaign.status}`}
									style={{
										gridColumn: `${segment.startCol + 1} / span ${segment.span}`,
										gridRow: segment.lane + 2,
									}}
									className={cn(
										"relative z-10 mx-1 mb-1 flex h-6 items-center gap-1 overflow-hidden px-1.5 text-xs font-medium transition-opacity hover:opacity-80",
										segment.continuesLeft ? "rounded-l-none" : "rounded-l-md",
										segment.continuesRight ? "rounded-r-none" : "rounded-r-md",
										BAR_STYLES[segment.campaign.status],
									)}
								>
									{segment.continuesLeft ? <span aria-hidden>←</span> : null}
									<span className="truncate">{segment.campaign.title}</span>
									{segment.continuesRight ? (
										<span className="ml-auto" aria-hidden>
											→
										</span>
									) : null}
								</Link>
							))}
						</div>
					);
				})}
			</div>

			{unscheduled.length > 0 ? (
				<div className="rounded-md border border-dashed border-line px-4 py-3">
					<p className="text-xs font-medium text-ink">Not scheduled</p>
					<p className="mt-0.5 text-xs text-ink-muted">
						These have no dates yet, so they cannot appear on the calendar.
					</p>
					<ul className="mt-2 flex flex-wrap gap-2">
						{unscheduled.map((campaign) => (
							<li key={campaign.id}>
								<Link
									to={`/campaigns/${campaign.id}`}
									className="flex items-center gap-2 rounded-md border border-line px-2 py-1 text-xs text-ink transition-colors hover:bg-raised"
								>
									{campaign.title}
									<Badge variant="outline">
										{campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
									</Badge>
								</Link>
							</li>
						))}
					</ul>
				</div>
			) : null}
		</section>
	);
};
